import { ReactNode, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SummaryStyle as SummaryStyleUI, HeadingBold, TabContainer, TabItem, SummaryScroll, Separated, Divider, CartCard, Icon, SummaryFooter, PrimaryCtaButton, SecondaryCtaButton, QuaternaryCtaButton, Button, Swatch } from "slate-ui-components";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { cleanSlate, optionToggle, VisualizerSlice } from "./store/reducers/visualizer";
import { complete, JSONValue } from "@zerolight-core/iframe-communications";
import { useShareDialog } from "./Dialogs/ShareDialog";
import { getBuildData } from "./helpers";
import { useBuildSession } from "./BuildSession";
import { OptionData, ZLVEVisualizerState } from "./types/VisualizerTypes";
import { useImageMedia } from "./utils/useImageMedia";
import { useDirect2D } from "./components/Direct2D";
import { JSONEquality } from "./utils/equal";
import { useItemDetailsDialog } from "./Dialogs/ItemDetailDialog";
import { restart } from "@zerolight-core/iframe-communications";
import styled from "styled-components";
import { useAnalytics } from "./AnalyticsProvider";
import { AppConfig } from "./config";
import { AnimationStates } from "@zerolight-core/libzl";

const SummaryStyle = styled(SummaryStyleUI)`
	align-items: center;

	& > *{
		max-width: 774px;
		width: 100%;
		box-sizing: border-box;
	}	
`

const Info = styled(Icon)`
	flex-shrink: 0;
	margin-right: 8px;
`
const ItemName = styled(Button)`
	color: ${({theme}) => theme.colours.text.primary};
	text-align: left;
`

export function Summary()
{
	const {record} = useAnalytics()
	const d2d = useDirect2D()
	const {current: build, masterModel} = useBuildSession()
	const [activeCategory, setActiveCategory] = useState("*");
	const share = useShareDialog("Summary")
	const intl = useIntl();
	const dispatch = useAppDispatch()
	const { environment, prcodes } = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as ZLVEVisualizerState, JSONEquality);
	const environmentAnimations = AppConfig.visualizer.configuration.optionsGroups.environments.options[environment]?.visualizerState?.forceAnimationStates as (AnimationStates | undefined)
	
	const { customWraps, appliedWraps } = useAppSelector(s => s.configuration.currentVisualizerConfig.configuration)
	const [ itemDetails, setItemDetails ] = useState<{groupId?: string, itemId?: string}>({})

	const itemDetailsDialog = useItemDetailsDialog(itemDetails.groupId, itemDetails.itemId, "summary")
	const openItemDetails = useCallback((groupId: string, itemId: string) => {
		setItemDetails({groupId, itemId})
		itemDetailsDialog.open()
	}, [itemDetailsDialog])

	const unfilteredCategories = useAppSelector(s => s.configuration.currentVisualizerConfig.categories)
	const selectedOptions = useAppSelector(s => s.configuration.selectedOptions)
	const optionGroups = useAppSelector(s => s.configuration.optionGroups)

	const categories = unfilteredCategories.filter(c => selectedOptions[c.group].filter(optionId => !optionGroups[c.group].options[optionId]?.hideInSummary)?.length > 0);
	
	const currentCategory = categories.find(c => c.id == activeCategory);
	const categoryIndex = categories.findIndex(c => c.id == activeCategory) + 1;
	const options = Object.entries(selectedOptions)
		.filter(([groupId]) => activeCategory == "*" ? categories.some(s => s.group == groupId) : currentCategory?.group == groupId)
		.flatMap(([groupId, optionIds]) => 
			optionIds.map(optionId => ({ groupId, optionId, option: optionGroups[groupId]?.options[optionId] })).filter(o => o.option)
		)
	
	useEffect(() =>	{
		if (!currentCategory && activeCategory != "*") {
			setActiveCategory("*");
		}
	}, [activeCategory, currentCategory])


	const reserveLabel = intl.formatMessage({id: "summary.reserve", defaultMessage: "RESERVE A BLANK SLATE"})
	const shareLabel = intl.formatMessage({id: "summary.share", defaultMessage: "SHARE"})
	const restartLabel = intl.formatMessage({id: "summary.restart", defaultMessage: "START NEW"})

	const onReserve = async () => {
		if(build) {
			const buildData = await getBuildData(intl, masterModel!, prcodes, environmentAnimations, environment, build, customWraps, appliedWraps, d2d, true)
			record?.(["Configurator", "Reserve"], {configuration: build.getSelectedItems(true).map(i => i.id), customWrap: Object.values(customWraps).find(w => w.selected) as unknown as JSONValue})
			complete(buildData)
		}
	}

	const onRestart = () => {
		//Reset build, return to configure mode?
		restart()
		dispatch(cleanSlate())
		record?.(["Configurator", "Restart"])
	}

    return <SummaryStyle style={{maxHeight: "calc(100vh - 38px)"}}>
        <span></span>
        <HeadingBold><FormattedMessage id="summary.title" defaultMessage="SUMMARY"/></HeadingBold>
		<TabContainer className="tabs" tabIndex={categoryIndex} onTabChange={(id) => setActiveCategory(id)}>
			<TabItem id="*" label={intl.formatMessage({id: "summary.categories.all", defaultMessage: "All"})} testId={"summary.category.all"} />
			{categories.map(s => <TabItem id={s.id} label={intl.formatMessage(s.displayName)} testId={`summary.category.${s.id}`} />)}
		</TabContainer>
        <SummaryScroll>
			{options.length > 0 ?
				<Separated by={<Divider />}>
					{options && options.filter(v => !v.option.hideInSummary).map(v =>
						<OptionCard option={v.option} groupId={v.groupId} optionId={v.optionId} openDetails={openItemDetails}/>
					)}
				</Separated>
				:
				<FormattedMessage id="summary.no_accessories" defaultMessage="The Blank Slate with no added accessories."/>
			}
        </SummaryScroll>
        <SummaryFooter>
            <PrimaryCtaButton testId="summary.reserve" label={reserveLabel} onClick={onReserve}>
				{reserveLabel}
				<Icon icon="Arrow" />
            </PrimaryCtaButton>
            <SecondaryCtaButton testId="summary.share" label={shareLabel} onClick={share.open}>
				{shareLabel}
				<Icon icon="Share" />
            </SecondaryCtaButton>
            <QuaternaryCtaButton testId="summary.restart" label={restartLabel} onClick={onRestart}>
				{restartLabel}
				<Icon icon="Restart" />
            </QuaternaryCtaButton>
        </SummaryFooter>
		{share.element}
		{itemDetailsDialog.element}
    </SummaryStyle>
}

function OptionCard(props: {option: OptionData, optionId: string, groupId: string, openDetails: (groupId: string, optionId: string) => void })
{
	const intl = useIntl();
	const dispatch = useAppDispatch();
	const thumbnail = useImageMedia(props.option.thumbnailSummary!);

	let name : ReactNode = intl.formatMessage(props.option.displayName)
	if(props.option.buildSessionOption || props.option.buildSessionUpdate) {
		name = <ItemName label={name as string} testId={`item-details-${props.optionId}`} onClick={() => { props.openDetails(props.groupId, props.optionId) }}><Info icon="Info"/>{name}</ItemName>
	}

	return <CartCard
		name={name}
		
		right={
			props.optionId != "TB009" && <Button
				label={intl.formatMessage({id: "summary.remove", defaultMessage: "Remove"})}
				onClick={() => { dispatch(optionToggle({ group: props.groupId, option: props.optionId })) }}
				testId={`summary.remove.${props.optionId}`}
			>
				<Icon icon="Trash" />
			</Button>
		}
		thumbnail={
			thumbnail.type === "image" ? 
				thumbnail?.url ?? "" 
				:
				<Swatch colour={thumbnail.colour} finish={thumbnail.finish}/>
		}
	/>
}